import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { userAdminService } from '../../services/userAdminService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const EditUserAdmin = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '', // opcional na edição
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchUserAdmin = async () => {
      if (!id) return;

      try {
        setInitialLoading(true);
        setError(null);

        const userAdmin = await userAdminService.getUserAdmin(Number(id));
        setFormData({
          name: userAdmin.name,
          email: userAdmin.email,
          password: '',
        });
      } catch (err) {
        setError('Erro ao carregar dados do administrador. Tente novamente mais tarde.');
        console.error('Error fetching user admin:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserAdmin();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        name: formData.name,
        email: formData.email,
        ...(formData.password ? { password: formData.password } : {}),
      };

      await userAdminService.updateUserAdmin(Number(id), updateData);
      navigate('/user-admins');
    } catch (err) {
      setError('Erro ao atualizar administrador. Tente novamente mais tarde.');
      console.error('Error updating user admin:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/user-admins')}
          variant="outlined"
        >
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Editar Administrador
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Nome"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Nova Senha (opcional)"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="Deixe em branco para manter a senha atual"
            />

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !formData.name.trim() || !formData.email.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/user-admins')}
                disabled={loading}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default EditUserAdmin; 